"use client"

import * as React from "react"

/**
 * AutoImportWorker - Background component that triggers JSON file imports
 *
 * This component runs in the background and periodically calls the worker API
 * to import JSON files from the configured folder. It only runs when:
 * - User is authenticated (has auth token)
 * - User has a selected workspace
 *
 * The interval is read from the settings API.
 */
export function AutoImportWorker() {
  const [isRunning, setIsRunning] = React.useState(false)
  const intervalRef = React.useRef<NodeJS.Timeout | null>(null)

  React.useEffect(() => {
    let isMounted = true

    const getWorkspaceId = () => localStorage.getItem("current_workspace_id")

    /**
     * Fetch the configured interval from settings
     */
    const fetchInterval = async (): Promise<number> => {
      try {
        const response = await fetch("/api/settings")

        if (!response.ok) return 60

        const data = await response.json()
        return data.data?.readIntervalMin || 60
      } catch {
        return 60
      }
    }

    /**
     * Trigger the worker to process JSON files
     */
    const triggerWorker = async () => {
      const workspaceId = getWorkspaceId()

      if (!workspaceId) {
        return
      }

      // Don't run if already running
      if (isRunning) return

      try {
        setIsRunning(true)

        const response = await fetch("/api/worker", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ workspaceId }),
        })

        if (response.ok) {
          const result = await response.json()
          if (result.data?.imported > 0) {
            console.log(`[AutoImportWorker] Imported ${result.data.imported} files`)
          }
        }
      } catch (error) {
        console.error("[AutoImportWorker] Error:", error)
      } finally {
        if (isMounted) {
          setIsRunning(false)
        }
      }
    }

    /**
     * Initialize the worker interval
     */
    const initWorker = async () => {
      // Get interval from settings
      const intervalMinutes = await fetchInterval()
      const intervalMs = intervalMinutes * 60 * 1000

      // Run immediately on init
      triggerWorker()

      // Set up interval
      intervalRef.current = setInterval(triggerWorker, intervalMs)

      console.log(`[AutoImportWorker] Started with ${intervalMinutes} minute interval`)
    }

    // Start the worker
    initWorker()

    // Cleanup on unmount
    return () => {
      isMounted = false
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  // This component renders nothing - it's purely for side effects
  return null
}
